/**
 * Tab Button Component for Navigation Interfaces
 * 
 * A reusable button component designed for tab-based navigation systems.
 * Provides visual feedback for active states and adapts styling based on context.
 * 
 * Features:
 * - Active state indication with accent color underline
 * - Context-aware styling (sticky vs regular navigation)
 * - Smooth color transitions for better user experience
 * - Responsive hover states
 * - Accessible button semantics
 * 
 * Visual States:
 * - Active: Accent color text with bottom border indicator
 * - Sticky context: White text with light hover effect
 * - Regular context: Dark text with darker hover effect
 * 
 * @param {boolean} isActive - Whether this tab is currently selected
 * @param {function} onClick - Click handler function for tab selection
 * @param {string} label - Display text for the tab button
 * @param {boolean} isSticky - Whether the tab is in a sticky/overlay context
 * 
 * @description Reusable tab button with context-aware styling and active state indication
 */
const TabButton = ({ isActive, onClick, label, isSticky }) => (
  <button 
    className={`
      px-3 sm:px-4 md:px-6 
      py-2 sm:py-2.5 md:py-3 
      font-semibold md:font-bold 
      text-sm sm:text-base md:text-lg 
      relative 
      transition-all duration-300 
      whitespace-nowrap
      flex-shrink-0
      ${
        isActive 
          ? 'text-accent-color' 
          : isSticky 
            ? 'text-white hover:text-gray-200' 
            : 'text-gray-700 hover:text-gray-900'
      }
    `}
    onClick={onClick}
    role="tab"
    aria-selected={isActive}
  >
    {label}
    {/* Active state indicator - bottom accent border */}
    {isActive && (
      <span className="absolute bottom-0 left-0 w-full h-0.5 md:h-1 bg-accent-color transition-all duration-300"></span>
    )}
  </button>
);

export default TabButton; 